'use client';

import { ChevronDown, Trash2 } from 'lucide-react';

import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { VerticalReorderingList } from '@/components/vertical-reordering-list';

import { cn } from '@/utils/styles';

import OtherOptionField from './other-option-field';
import { AddOptionButton } from './shared/add-option-button';
import { OptionRow } from './shared/option-row';
import { IOption, useFieldOptions } from './shared/use-field-options';

interface IDropdownFieldProps {
	options: string[];
	selectedOption?: string;
	onSelectOption?: (option: string | undefined) => void;
	onChangeOptions?: (updatedOptions: string[]) => void;
	editMode?: boolean;
	disabled?: boolean;
	isFocusedInput?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
	allowOther?: boolean;
	onAllowOtherChange?: (value: boolean) => void;
}

interface ImperativeHandleProps {
	focusOnFirstOption: () => void;
}

const OTHER_OPTION_ID = 'other#$!@ *&';
const DROPDOWN_MOVE_ICON_CLASS =
	'absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover/option:opacity-100 text-gray-400';
const DROPDOWN_DELETE_BUTTON_CLASS =
	'invisible absolute -right-2 h-5 w-5 p-0 text-slate-400 group-hover/option:visible';

const DropdownField = forwardRef<ImperativeHandleProps, IDropdownFieldProps>(
	(
		{
			options,
			selectedOption,
			onSelectOption,
			onChangeOptions,
			editMode,
			isFocusedInput,
			onFocus,
			onBlur,
			disabled,
			allowOther = false,
			onAllowOtherChange,
		},
		ref
	) => {
		const [openDropdown, setOpenDropdown] = useState(false);
		const [otherSelected, setOtherSelected] = useState(false);

		const {
			optionsCopy,
			displayDeleteButton,
			displayReorderIcon,
			displayAddOption,
			focusOnFirstOption,
			registerOptionRef,
			handleOnReorderOptions,
			handleOnDeleteOption,
			onClickAddOption,
			handleInputOptionChange,
			handleInputOptionFocus,
			handleInputOptionBlur,
			handleInputOptionKeyDown,
		} = useFieldOptions({ options, onChangeOptions, editMode, isFocusedInput, onFocus, onBlur });

		useImperativeHandle(ref, () => ({ focusOnFirstOption }), [focusOnFirstOption]);

		const handleOnSelectOption = (optionValue: string) => {
			onSelectOption?.(optionValue);
			setOtherSelected(false);
		};

		const handleOtherOptionChange = useCallback(
			(value: string) => {
				handleOnSelectOption(value);
				setOtherSelected(true);
			},
			[handleOnSelectOption]
		);

		const handleOtherSelect = useCallback(
			(selected: boolean) => {
				handleOnSelectOption('');
				setOtherSelected(selected);
			},
			[handleOnSelectOption]
		);

		if (!editMode) {
			return (
				<div className="flex items-end gap-3">
					<DropdownMenu
						open={openDropdown}
						onOpenChange={setOpenDropdown}
					>
						<DropdownMenuTrigger asChild>
							<Button
								variant={'ghost'}
								className={cn(
									'flex h-auto w-60 justify-between gap-2 overflow-hidden border border-input p-3 text-base font-normal leading-6 text-slate-400 hover:bg-transparent',
									!!selectedOption || otherSelected ? 'text-primary' : ''
								)}
								disabled={disabled}
							>
								<span className="inline-block flex-1 truncate text-start">
									{otherSelected ? 'Other' : selectedOption ? selectedOption : 'Choose'}
								</span>
								<ChevronDown
									className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openDropdown ? 'rotate-180' : ''}`}
								/>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="start"
							onCloseAutoFocus={(e) => {
								e.preventDefault();
							}}
						>
							{[
								...optionsCopy,
								(allowOther ? { id: OTHER_OPTION_ID, name: 'Other' } : null) as unknown as IOption,
							]
								?.filter(Boolean)
								?.map((option) => (
									<DropdownMenuItem
										className="flex min-w-40 max-w-80 cursor-pointer p-3"
										key={option.id}
										onClick={() =>
											option.id === OTHER_OPTION_ID
												? handleOtherSelect(true)
												: handleOnSelectOption(option.name)
										}
									>
										<span className="text-base font-normal leading-6 text-primary">
											{option.name}
										</span>
									</DropdownMenuItem>
								))}
						</DropdownMenuContent>
					</DropdownMenu>

					{otherSelected && (
						<Input
							className="w-full max-w-[300px] rounded-none border-b border-l-0 border-r-0 border-t-0 outline-none focus-visible:border-primary focus-visible:shadow-none focus-visible:shadow-white focus-visible:ring-0"
							placeholder="Type here"
							onChange={(e) => handleOtherOptionChange?.(e.target.value)}
						/>
					)}
				</div>
			);
		}

		return (
			<div className="flex w-full max-w-full flex-col">
				<VerticalReorderingList
					items={optionsCopy}
					onReorder={handleOnReorderOptions}
					className="w-full"
					getItemId={(option) => option.id}
					renderItem={(option, index) => (
						<OptionRow
							key={option.id}
							option={option}
							index={index}
							editMode={editMode}
							itemClassName="px-0"
							moveIconClassName={DROPDOWN_MOVE_ICON_CLASS}
							deleteButtonClassName={DROPDOWN_DELETE_BUTTON_CLASS}
							displayDeleteButton={displayDeleteButton}
							displayReorderIcon={displayReorderIcon}
							registerRef={registerOptionRef(option.id)}
							onChangeValue={handleInputOptionChange(option.id)}
							onFocus={handleInputOptionFocus}
							onBlur={handleInputOptionBlur}
							onKeyDown={handleInputOptionKeyDown(option.id)}
							onDelete={handleOnDeleteOption(option.id)}
						/>
					)}
				/>

				{displayAddOption && (
					<AddOptionButton
						onAddOption={onClickAddOption()}
						onAddOther={() => onAllowOtherChange?.(true)}
						hideOther={allowOther}
						hideCircle
					/>
				)}

				{allowOther && (
					<div className="group flex items-start justify-between gap-2">
						<OtherOptionField
							hideIcon
							disabled={editMode}
							selected={otherSelected && !editMode}
							onSelect={handleOtherSelect}
							onInputChange={(value) => (editMode ? null : handleOtherOptionChange(value))}
						/>

						{displayDeleteButton && (
							<Button
								variant={'ghost'}
								className="opacity-hover:bg-transparent pointer-events-none h-5 w-5 p-0 text-slate-400 opacity-0 group-hover:pointer-events-auto group-hover:opacity-100"
								onClick={() => onAllowOtherChange?.(false)}
							>
								<Trash2 className="h-5 w-5" />
							</Button>
						)}
					</div>
				)}
			</div>
		);
	}
);

DropdownField.displayName = 'DropdownField';

export default DropdownField;
